import { NetworkStatus } from "@apollo/client";
import type { ApolloClient } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { PowerAnalysisDocumentsDocument, SortOrder } from "@/graphql/generated/operations";

const PAGE_INPUT = { first: 100, order: { createdAt: SortOrder.Asc } };

export type AnalysedDocumentCountState =
  | { status: "unavailable" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; count: number };

export function refreshAnalysedDocumentCount(client: ApolloClient) {
  return client.refetchQueries({ include: [PowerAnalysisDocumentsDocument] });
}

export function useAnalysedDocumentCount(enabled: boolean) {
  const requestedCursor = useRef<string | null>(null);
  const [failedCursor, setFailedCursor] = useState<string | null>(null);
  const { data, loading, error, networkStatus, fetchMore } = useQuery(
    PowerAnalysisDocumentsDocument,
    {
      skip: !enabled,
      // A separate order keeps this lightweight connection apart from document pickers.
      variables: { input: PAGE_INPUT },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
  );
  const pageInfo = data?.documents.pageInfo;

  useEffect(() => {
    if (!enabled || networkStatus === NetworkStatus.refetch || networkStatus === NetworkStatus.loading) {
      requestedCursor.current = null;
      return;
    }
    const cursor = pageInfo?.endCursor;
    if (loading || error || !pageInfo?.hasNextPage || !cursor || requestedCursor.current === cursor) return;
    requestedCursor.current = cursor;
    // A failed page stays incomplete and unavailable until the next refetch.
    void fetchMore({
      variables: { input: { ...PAGE_INPUT, after: cursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        ...fetchMoreResult,
        documents: {
          ...fetchMoreResult.documents,
          nodes: [
            ...new Map(
              [...previous.documents.nodes, ...fetchMoreResult.documents.nodes].map(
                (document) => [document.id, document],
              ),
            ).values(),
          ],
        },
      }),
    }).then(() => setFailedCursor(null)).catch(() => setFailedCursor(cursor));
  }, [enabled, loading, error, networkStatus, pageInfo?.hasNextPage, pageInfo?.endCursor, fetchMore]);

  if (!enabled) return { status: "unavailable" } as const;
  if (error || (!loading && pageInfo?.hasNextPage && failedCursor === pageInfo.endCursor))
    return { status: "error" } as const;
  if (loading || !data || pageInfo?.hasNextPage) return { status: "loading" } as const;
  const count = new Set(
    data.documents.nodes
      .filter((document) => document.powersExtractedAt != null)
      .map((document) => document.id),
  ).size;
  return { status: "ready", count } as const;
}
